import React from "react";
import { useNavigate } from "react-router-dom";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { FiltersDropdown } from "reusableComponents";
import { iconKWhite } from "assets/icons";

// ------------------------------------------------------------
// Format ISO date (YYYY-MM-DD or Date) → dd/mm/yyyy
// ------------------------------------------------------------
const formatDateDMY = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const PromoCodeCard = ({ promo, handleDelete, handleStatusChange }) => {
  const navigate = useNavigate();
  const { t } = useTranslation("global");

  const statusOptions = [
    { value: true, label: t("dashboard.promoCodes.statusActive", "Active") },
    { value: false, label: t("dashboard.promoCodes.statusInactive", "Inactive") },
  ];

  const statusIconClass = promo.active ? styles.activeIcon : styles.inactiveIcon;

  // ── Conditional data presence checks ──
  // Older promo codes don't have `customer_name`, `places`, or `start_date`.
  const hasCustomerName =
    typeof promo.customer_name === "string" && promo.customer_name.trim() !== "";
  const appliedSpaces = Array.isArray(promo.places) ? promo.places : [];
  const hasAppliedSpaces = appliedSpaces.length > 0;
  const hasStartDate = !!promo.start_date;

  return (
    <div key={promo.id} className={styles.promoCard}>
      <div className={styles.promoCardHeader}>
        <button
          onClick={() => navigate(`/dashboard/promo-code/${promo.id}`)}
          className={styles.editButton}
        >
          {t("dashboard.spacesManagement.edit")}
        </button>
        <button
          onClick={() => handleDelete(promo.id)}
          className={styles.deleteButton}
        >
          {t("dashboard.spacesManagement.delete")}
        </button>
      </div>

      <div className={styles.promoDetails}>
        <div className={styles.promoDetailRow}>
          <div className={styles.promoDetailCell}>
            <p className={styles.promoDetailTitle}>
              {t("dashboard.promoCodes.codeShort")}
            </p>
            {promo.title || "—"}
          </div>

          <div className={styles.promoDetailCell}>
            <p className={styles.promoDetailTitle}>
              {t("dashboard.promoCodes.percent")}
            </p>
            {promo.percentage}%
          </div>

          {hasCustomerName && (
            <div className={styles.promoDetailCell}>
              <p className={styles.promoDetailTitle}>
                {t("dashboard.promoCodes.customerName")}
              </p>
              {promo.customer_name}
            </div>
          )}

          <div className={styles.promoDetailCell}>
            <p className={styles.promoDetailTitle}>
              {t("dashboard.promoCodes.customerEmail")}
            </p>
            {promo.customer_email || "—"}
          </div>

          {hasStartDate && (
            <div className={styles.promoDetailCell}>
              <p className={styles.promoDetailTitle}>
                {t("dashboard.promoCodes.startDate")}
              </p>
              {formatDateDMY(promo.start_date)}
            </div>
          )}

          <div className={styles.promoDetailCell}>
            <p className={styles.promoDetailTitle}>
              {t("dashboard.promoCodes.expirationDate")}
            </p>
            {formatDateDMY(promo.expiration_date)}
          </div>

          {hasAppliedSpaces && (
            <div className={styles.promoDetailCell}>
              <p className={styles.promoDetailTitle}>
                {t("dashboard.promoCodes.appliedSpaces")}
              </p>
              <div className={styles.appliedSpacesList}>
                {appliedSpaces.map((place) => (
                  <span key={place.id} className={styles.appliedSpaceTag}>
                    {place.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.statusWrapper}>
        <FiltersDropdown
          options={statusOptions}
          selected={promo.active}
          onSelect={(value) => handleStatusChange(promo.id, value)}
          placeholder={t("dashboard.promoCodes.selectStatus", "Select Status")}
        />
        <img
          src={iconKWhite}
          alt="k icon"
          className={`${styles.statusIcon} ${statusIconClass}`}
        />
      </div>
    </div>
  );
};

export default PromoCodeCard;